import { Skeleton } from '@knack/asterisk-react';

import { cn } from '@/utils/tailwind';
import { useThemingContext } from '@/context/ThemingContext';

export function TableSkeleton() {
  const { theme } = useThemingContext();

  const tableMaxHeight = theme.tables.hasMaxHeight
    ? theme.tables.maxHeightPx
    : 'calc(100vh - 50px)';
  const rowSpacingClasses = cn({
    'px-2 py-1': theme.tables.rowSpacing === 'compact',
    'p-2': theme.tables.rowSpacing === 'default',
    'px-2 py-3': theme.tables.rowSpacing === 'comfortable'
  });

  return (
    <div className="w-full opacity-60" data-testid="table-skeleton">
      {/* Header */}
      <div className="mb-4">
        <Skeleton className="mb-4 h-6 w-48" />

        {/* Filter and Search Bar */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-64" />
        </div>
      </div>

      {/* Table */}
      <div
        className={cn('kn-table-view-container relative w-full overflow-hidden border', {
          'rounded-lg': theme.appearance.corners === 'rounded'
        })}
      >
        <div
          className={cn('kn-table-view-wrapper overflow-auto border-b-0', {
            // If we have scrollbar, we need to remove the border radius from the last row
            '[&_th:nth-child(n+2):last-child]:rounded-none': theme.appearance.corners === 'rounded'
          })}
          style={{ maxHeight: tableMaxHeight }}
        >
          {/* Table Header */}
          <div className="border-b bg-gray-50">
            <div className="grid h-auto grid-cols-7 gap-4 px-2 py-1">
              <Skeleton className="w-25 h-5" />
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-5 w-20" />
              <Skeleton className="w-25 h-5" />
              <Skeleton className="h-5 w-20" />
              <div className="flex justify-center">
                <Skeleton className="h-4 w-12" />
              </div>
              <div className="flex justify-center">
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </div>

          {/* Table Rows */}
          {[...Array(8)].map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="border-b last:border-b-0">
              <div className={cn('grid grid-cols-7 items-center gap-4', rowSpacingClasses)}>
                <Skeleton className="h-4 w-20" />
                <div className="flex items-center">
                  <Skeleton className="h-6 w-12 rounded-full" />
                </div>
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-6" />
                <Skeleton className="w-18 h-4" />
                <div className="flex justify-center">
                  <Skeleton className="size-8 rounded" />
                </div>
                <div className="flex justify-center">
                  <Skeleton className="size-8 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="mt-2 flex items-center justify-between border-t px-2 py-1">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-8 w-32" />
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
